import { gql } from '@apollo/client';
import type { TypedDocumentNode } from '@apollo/client';
import { faArrowsRotate, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import type { CSSProperties } from 'react';
import { FormattedMessage, FormattedTime } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BrandM, LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import { StarPackContentDialog } from 'components/commonCard/StarPackContentDialog';

import type { StarPackTitle_cardPack } from './__generated__/index.graphql';

const Header = styled(SBHorizontal)`
  width: 100%;
  align-items: flex-start;
`;
const PlayerNameWrapper = styled(Horizontal)`
  align-items: baseline;
`;
const RotationAtWrapper = styled(Horizontal)`
  gap: 2px;
  color: var(--c-yellow-400);
`;

type Props = {
  cardPack: StarPackTitle_cardPack;
};

export const StarPackTitle = ({ cardPack }: Props) => {
  const [contentDialogOpen, setContentDialogOpen] = useState(false);

  const { rewardedByTask } = cardPack;

  if (!rewardedByTask || !isType(rewardedByTask, 'StarPackTask')) {
    return null;
  }

  const { nextStarPlayerRotationAt, starPlayer } = rewardedByTask;
  const {
    anyPlayer: { displayName: starPlayerDisplayName },
  } = starPlayer;
  return (
    <>
      <Header>
        <Vertical gap={0.5}>
          <BrandM>
            <FormattedMessage id="StarPack.title2" defaultMessage="Star Pack" />
          </BrandM>
          <PlayerNameWrapper gap={0.5}>
            <LabelL
              className="text-gradient-color"
              style={
                {
                  '--text-gradient-color':
                    'linear-gradient(315deg, #868a8a 14.64%, #e8ecee 85.36%)',
                } as CSSProperties
              }
            >
              {starPlayerDisplayName}
            </LabelL>
            <LabelS color="var(--c-yellow-400)">
              <Tooltip
                title={
                  <FormattedTime
                    dateStyle="short"
                    day="numeric"
                    month="short"
                    year="numeric"
                    value={nextStarPlayerRotationAt}
                  />
                }
              >
                <RotationAtWrapper>
                  <FontAwesomeIcon icon={faArrowsRotate} size="xs" />
                  <TimeLeft time={nextStarPlayerRotationAt} />
                </RotationAtWrapper>
              </Tooltip>
            </LabelS>
          </PlayerNameWrapper>
        </Vertical>
        <LabelS color="var(--c-nd-600)">
          <ButtonBase
            onClick={() => {
              setContentDialogOpen(true);
            }}
          >
            <Horizontal gap={0.5}>
              <FormattedMessage {...glossary.learnMore} />
              <FontAwesomeIcon icon={faInfoCircle} />
            </Horizontal>
          </ButtonBase>
        </LabelS>
      </Header>
      <StarPackContentDialog
        open={contentDialogOpen}
        onClose={() => {
          setContentDialogOpen(false);
        }}
        cardPack={cardPack}
      />
    </>
  );
};

StarPackTitle.fragments = {
  cardPack: gql`
    fragment StarPackTitle_cardPack on CardPack {
      slug
      rewardedByTask {
        id
        ... on StarPackTask {
          id
          nextStarPlayerRotationAt
          starPlayer {
            id
            anyPlayer {
              slug
              displayName
            }
          }
        }
      }
      ...StarPackContentDialog_cardPack
    }
    ${StarPackContentDialog.fragments.cardPack}
  ` as TypedDocumentNode<StarPackTitle_cardPack>,
};
